/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState, useMemo, useEffect } from 'react'
import {
  Bleed,
  Button,
  Checkbox,
  Divider,
  Expander,
  ExpanderHeader,
  ExpanderContent,
  Row,
  Separator,
  Toggle,
  ToggleGroup,
} from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { PlaceSuggestNEXT, useStandardSuggestNEXT } from '@fs/zion-standards'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'

import { useFeatureFlag } from '@fs/zion-flags'
import { usePermission } from '@fs/zion-permissions'
import helpers from '../../helpers'
import styles from '../../Coverages.styles'
import transl from './EditableCoverage.transl'
import { useCoverages, useGroupDataForm, useCheckboxes } from '../../../../hooks'
import { parseYearRangeFromPlaceSuggest } from '../editableCoverageDate/dateRangeValidationUtils'
import EditableCoverageRecordType from '../editableCoverageRecordType/EditableCoverageRecordType'
import EditableCoverageDate from '../editableCoverageDate/EditableCoverageDate'

const toggleButtonCss = css`
  margin-top: 12px;

  [class^='containerCss'] {
    margin-bottom: unset;
  }
`
const pushDown = css`
  margin-top: 12px;
`

export default function EditableCoverage({
  forVolumeSet = false,
  place,
  placeIndex,
  recordTypes,
  open,
  handleToggleOpen,
  allRecordTypes,
}) {
  const {
    setPlace,
    addRecordType,
    coverageCheckboxes,
    setApplyToChildren,
    handleRecordTypeCheckboxChange,
    handlePlaceCheckboxChange,
    handleDateCheckboxChange,
    validatePlace,
    validateRecordType,
    validateDate,
  } = useCoverages({ forVolumeSet })
  const { deleteCoveragesMode } = useGroupDataForm()
  const { deleteMode: deleteCreatorMode } = useCheckboxes()
  const [applyToChildrenToggledIndexes, setApplyToChildrenToggledIndexes] = useState(
    new Array(recordTypes.length).fill(false)
  )

  const showProvisionalPlaces = useFeatureFlag('lightyear_sharedImageViewer_provisionalPlacesInCoverages')
  const lightyearProvisionalPlacesAuthorized = usePermission('Standards_Prm_ProposePlaces', 'InfinityContext')
  const disabled = deleteCoveragesMode || deleteCreatorMode
  // infer place checked value if all recordTypes are checked
  const percentChecked = helpers.getPercentRecordTypesTrue(coverageCheckboxes[placeIndex])
  const placeChecked = percentChecked === 1

  const allowProvisionalPlaces = useMemo(() => {
    const [permission, loading] = lightyearProvisionalPlacesAuthorized
    if (showProvisionalPlaces?.isOn && loading === 'loaded' && permission) return true

    return false
  }, [showProvisionalPlaces, lightyearProvisionalPlacesAuthorized])

  const id = place?.id
  const name = place?.fullName
  const initialState = {
    userInput: name,
    fieldValue: name,
    selectedItem: {
      isStandard: true,
      primaryText: name,
      standard: {
        standardizedText: name,
        id: String(id),
      },
    },
  }
  // Using the useAutosuggest hook rather than the useStandardSuggestNEXT hook to avoid a bug
  const config = useStandardSuggestNEXT({
    initialState,
  })
  const { selectedItem } = config

  const primaryText = selectedItem?.primaryText
  const placeId = selectedItem?.standard?.id || place?.id

  const { fromYear: fromYearParsed, toYear: toYearParsed } = useMemo(
    () => parseYearRangeFromPlaceSuggest(selectedItem?.standard?.yearRange),
    [selectedItem?.standard?.yearRange]
  )
  const fromYear =
    fromYearParsed ?? selectedItem?.fromYear ?? selectedItem?.standard?.fromYear ?? selectedItem?.rep?.fromYear
  const toYear = toYearParsed ?? selectedItem?.toYear ?? selectedItem?.standard?.toYear ?? selectedItem?.rep?.toYear

  useEffect(() => {
    if (!primaryText || !placeId) return
    if (primaryText !== name) {
      setPlace(placeIndex, primaryText, placeId, undefined, fromYear, toYear)
    }
  }, [primaryText, placeId, selectedItem, placeIndex, setPlace, name, fromYear, toYear])

  function handleToggle(target, recordTypeIndex) {
    const newIndexes = [...applyToChildrenToggledIndexes]
    newIndexes[recordTypeIndex] = target.checked
    setApplyToChildrenToggledIndexes(newIndexes)
    setApplyToChildren(placeIndex, recordTypeIndex, target.checked)
  }

  return (
    <li>
      <Expander open={open} setOpen={() => handleToggleOpen(place?.id)}>
        <ExpanderHeader>
          <Row alignY="middle">
            <Checkbox
              aria-label={transl.selectPlace()}
              name="place"
              data-testid="EditableCoverage.placeCheckbox"
              checked={placeChecked}
              mixedState={percentChecked > 0 && percentChecked < 1}
              onChange={() => handlePlaceCheckboxChange(placeIndex, !placeChecked)}
              onClick={(e) => e.stopPropagation()}
            />
            <div css={styles.placeCheckboxText(open)}>{place?.fullName}</div>
          </Row>
        </ExpanderHeader>
        <ExpanderContent>
          <div css={styles.singleIndent}>
            <PlaceSuggestNEXT
              config={config}
              helpMessage={<LearnMore text={transl.placeHelpText()} />}
              disabled={disabled}
              showListWhenFocused
              errorMessage={validatePlace(placeIndex)}
              isProposed={allowProvisionalPlaces}
              clearable
              label={transl.placeAll()}
            />

            {recordTypes?.map((rt, recordTypeIndex) => (
              <div key={rt.id}>
                <Divider aria-hidden />
                <Separator size="xs" aria-hidden />
                <Row alignItemsY="top" key={rt.id}>
                  <div css={pushDown}>
                    <Checkbox
                      aria-label={transl.selectRecordType(rt.name)}
                      data-testid={`EditableCoverage.RecordTypeCheckbox-${rt.id}`}
                      name={String(rt.id)}
                      checked={coverageCheckboxes[placeIndex]?.[recordTypeIndex]?.recordTypeChecked}
                      onChange={({ target }) =>
                        handleRecordTypeCheckboxChange(placeIndex, recordTypeIndex, target.checked)
                      }
                    />
                  </div>
                  <Bleed top bottom>
                    <EditableCoverageRecordType
                      errorMessage={validateRecordType(placeIndex, recordTypeIndex)}
                      forVolumeSet={forVolumeSet}
                      placeId={placeId}
                      recordType={rt}
                      disabled={disabled}
                      allRecordTypes={allRecordTypes}
                    />
                    <EditableCoverageDate
                      errorMessage={validateDate(placeIndex, recordTypeIndex)}
                      forVolumeSet={forVolumeSet}
                      placeId={placeId}
                      recordType={rt}
                      disabled={disabled}
                      datesChecked={coverageCheckboxes[placeIndex]?.[recordTypeIndex]?.datesChecked}
                      onDateChecked={(dateIndex, value) =>
                        handleDateCheckboxChange(placeIndex, recordTypeIndex, dateIndex, value)
                      }
                    />
                    {forVolumeSet && (
                      <div css={toggleButtonCss}>
                        <ToggleGroup>
                          <Toggle
                            checked={applyToChildrenToggledIndexes[recordTypeIndex]}
                            label={transl.applyToAllVolumes()}
                            name="toggleMe"
                            onChange={({ target }) => handleToggle(target, recordTypeIndex)}
                          />
                        </ToggleGroup>
                      </div>
                    )}
                  </Bleed>
                </Row>
              </div>
            ))}
            {forVolumeSet && <Divider aria-hidden />}
            <Button Icon={ContentAdd} onClick={() => addRecordType(place?.id)} disabled={disabled}>
              {transl.recordType()}
            </Button>
            <Separator size="xxs" aria-hidden />
          </div>
          <Divider fullWidth aria-hidden />
        </ExpanderContent>
      </Expander>
    </li>
  )
}

const learnMoreStyle = css`
  font-size: 0.9em;
`

function LearnMore({ text }) {
  return <span css={learnMoreStyle}>{text}</span>
}

EditableCoverage.propTypes = {
  open: PropTypes.bool,
  handleToggleOpen: PropTypes.func,
  forVolumeSet: PropTypes.bool,
  place: PropTypes.shape({
    id: PropTypes.string,
    fullName: PropTypes.string,
  }),
  placeIndex: PropTypes.number,
  recordTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      date: PropTypes.arrayOf(
        PropTypes.shape({
          fromTimestamp: PropTypes.number,
          toTimestamp: PropTypes.number,
          gedcomX: PropTypes.string,
        })
      ),
    })
  ),
  allRecordTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
}
